<script setup lang="ts">
import type {
  Attachment,
  FineUploaderUpload,
} from '@/models/current-student-advanced-standing-form/api'
import FineUploaderModalUpload from '@/components/current-student-advanced-standing-form/upload/FineUploaderModalUpload.vue'
import SavedAttachmentTable from '@/components/current-student-advanced-standing-form/upload/SavedAttachmentTable.vue'

defineProps<{
  currentWorkExperienceAttachments: Attachment[]
  workExperienceAttachmentDeleteIds: number[]
  showWorkExperienceFineUploader: boolean
  visibleWorkExperienceNewAttachments: FineUploaderUpload[]
  workExperienceUploadDropActive: boolean
  workExperienceUploadAlertMessage: string
  deleteDisabled: boolean
  uploadThumbnailUrl: (attachment: FineUploaderUpload) => string
}>()

const emit = defineEmits<{
  stageWorkExperienceAttachmentDelete: [attachment: Attachment]
  workExperienceUploadDragEnter: [event: DragEvent]
  workExperienceUploadDragOver: [event: DragEvent]
  workExperienceUploadDragLeave: [event: DragEvent]
  workExperienceUploadDrop: [event: DragEvent]
  triggerUploadInput: [event: MouseEvent]
  uploadButtonKeydown: [event: KeyboardEvent]
  uploadButtonHover: [event: MouseEvent, hover: boolean]
  uploadButtonFocus: [event: FocusEvent, focused: boolean]
  workExperienceAttachmentSelected: [event: Event]
  cancelStagedUpload: [attachment: FineUploaderUpload]
  retryWorkExperienceAttachment: [attachment: FineUploaderUpload]
  deleteStagedWorkExperienceAttachment: [attachment: FineUploaderUpload]
  closeWorkExperienceUploadAlert: []
}>()
</script>

<template>
  <div class="form-field">
    <label>Attachments</label>
    <p>Limit: 4 attachments.</p>
    <SavedAttachmentTable
      tbody-id="workExperienceAttachmentUploadTable"
      :attachments="currentWorkExperienceAttachments"
      :deleted-attachment-ids="workExperienceAttachmentDeleteIds"
      :delete-disabled="deleteDisabled"
      @stage-delete="emit('stageWorkExperienceAttachmentDelete', $event)"
    />
  </div>

  <div class="form-field">
    <FineUploaderModalUpload
      uploader-id="workExperienceAttachmentFineUploader"
      :visible="showWorkExperienceFineUploader"
      :attachments="visibleWorkExperienceNewAttachments"
      :drop-active="workExperienceUploadDropActive"
      :alert-message="workExperienceUploadAlertMessage"
      :upload-thumbnail-url="uploadThumbnailUrl"
      @drag-enter="emit('workExperienceUploadDragEnter', $event)"
      @drag-over="emit('workExperienceUploadDragOver', $event)"
      @drag-leave="emit('workExperienceUploadDragLeave', $event)"
      @drop="emit('workExperienceUploadDrop', $event)"
      @trigger-upload-input="emit('triggerUploadInput', $event)"
      @upload-button-keydown="emit('uploadButtonKeydown', $event)"
      @upload-button-hover="(event, hover) => emit('uploadButtonHover', event, hover)"
      @upload-button-focus="(event, focused) => emit('uploadButtonFocus', event, focused)"
      @file-selected="emit('workExperienceAttachmentSelected', $event)"
      @cancel-staged-upload="emit('cancelStagedUpload', $event)"
      @retry-upload="emit('retryWorkExperienceAttachment', $event)"
      @delete-staged-upload="emit('deleteStagedWorkExperienceAttachment', $event)"
      @close-alert="emit('closeWorkExperienceUploadAlert')"
    />
  </div>
</template>
